import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SpAccount } from '../SpAccountContext';
import { API_BASE, downloadExport } from '../api';

interface Props { account: SpAccount; marketplaceId: number | null; }

type InventoryTab = 'fba' | 'fbm';

interface FbaItem {
  id: string; seller_sku: string; asin: string | null; fn_sku: string | null;
  product_name: string | null; condition: string | null; marketplace_id: number;
  fulfillable_quantity: number; inbound_working_quantity: number;
  inbound_shipped_quantity: number; inbound_receiving_quantity: number;
  reserved_customer_orders: number; reserved_fc_transfers: number; reserved_fc_processing: number;
  unfulfillable_quantity: number; researching_quantity: number; synced_at: string;
}

interface FbmItem {
  id: string; seller_sku: string; asin: string | null; listing_id: string | null;
  item_name: string | null; price: number | null; quantity: number;
  pending_quantity: number; open_date: string | null; item_condition: string | null;
  fulfillment_channel: string | null; merchant_shipping_group: string | null;
  status: string | null; marketplace_id: number; synced_at: string;
}

type JobProgress = { status: string; records_fetched: number; error?: string };

const ORDER_REPORTS = [
  { value: '', label: 'Orders API (legacy)' },
  { value: 'GET_XML_ALL_ORDERS_DATA_BY_ORDER_DATE_GENERAL', label: 'By Order Date (daily)' },
  { value: 'GET_XML_ALL_ORDERS_DATA_BY_LAST_UPDATE_GENERAL', label: 'By Last Update (every 2 h)' },
];

function useJobPoller(
  headers: Record<string, string>,
  onDone: () => void,
): [JobProgress | null, (jobId: string) => void, () => void] {
  const [progress, setProgress] = useState<JobProgress | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const start = (jobId: string) => {
    setProgress({ status: 'running', records_fetched: 0 });
    pollRef.current = setInterval(async () => {
      try {
        const r = await fetch(`${API_BASE}/api/v1/sp-api/jobs/${jobId}`, { headers });
        const d = await r.json();
        setProgress({ status: d.status, records_fetched: d.records_fetched, error: d.error });
        if (d.status !== 'running') { clearInterval(pollRef.current!); onDone(); }
      } catch { /* ignore */ }
    }, 2000);
  };

  const reset = () => { if (pollRef.current) clearInterval(pollRef.current); setProgress(null); };
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);
  return [progress, start, reset];
}

function JobStatus({ progress, noun }: { progress: JobProgress | null; noun: string }) {
  if (!progress) return null;
  const color = progress.status === 'completed' ? 'var(--success)'
    : progress.status === 'failed' ? 'var(--danger)' : 'var(--text-muted)';
  const msg = progress.status === 'running' ? `Fetched ${progress.records_fetched} ${noun}…`
    : progress.status === 'completed' ? `✓ Done — ${progress.records_fetched} ${noun}`
    : `✗ ${progress.error}`;
  return <span style={{ fontSize: 13, color }}>{msg}</span>;
}

// ── FBA Panel ─────────────────────────────────────────────────────────────────

const FbaPanel: React.FC<{ account: SpAccount; marketplaceId: number | null; headers: Record<string, string> }> = ({ account, marketplaceId, headers }) => {
  const [items, setItems] = useState<FbaItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  // Independent stock-state facets; combined as OR server-side. Values:
  // 'in_stock' | 'out_of_stock' | 'low_stock'.
  const [stockFilters, setStockFilters] = useState<string[]>([]);
  const toggleStock = (v: string) => setStockFilters(s => s.includes(v) ? s.filter(x => x !== v) : [...s, v]);
  const [pulling, setPulling] = useState(false);
  const PAGE_SIZE = 50;

  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const [progress, startPoller, resetPoller] = useJobPoller(headers, () => { setPulling(false); load(1); });

  const load = useCallback(async (
    p = 1,
    overrides?: Partial<{ search: string; stockFilters: string[]; sortKey: string | null; sortDir: 'asc' | 'desc' }>,
  ) => {
    setLoading(true);
    const sk = overrides?.sortKey !== undefined ? overrides.sortKey : sortKey;
    const sd = overrides?.sortDir ?? sortDir;
    const s  = (overrides?.search ?? search).trim();
    const sf = overrides?.stockFilters ?? stockFilters;
    const params = new URLSearchParams({ page: String(p), page_size: String(PAGE_SIZE) });
    if (marketplaceId) params.set('marketplace_id', String(marketplaceId));
    if (s) params.set('search', s);
    sf.forEach(v => params.append('stock_status', v));
    if (sk) { params.set('sort_key', sk); params.set('sort_dir', sd); }
    try {
      const res = await fetch(`${API_BASE}/api/v1/sp-api/accounts/${account.seller_id}/inventory?${params}`, { headers });
      const data = await res.json();
      setItems(data.data ?? []);
      setTotal(data.total ?? 0);
      setPage(p);
    } catch { /* keep existing */ } finally { setLoading(false); }
  }, [account.seller_id, marketplaceId, search, stockFilters, sortKey, sortDir]);

  useEffect(() => { load(1); }, [account.seller_id, marketplaceId]);

  const startPull = async () => {
    setPulling(true); resetPoller();
    try {
      const mkIds = marketplaceId ? [marketplaceId] : account.marketplaces.map(m => m.id);
      const res = await fetch(`${API_BASE}/api/v1/sp-api/accounts/${account.seller_id}/pull/inventory`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ marketplace_ids: mkIds }),
      });
      const data = await res.json();
      if (data.job_id) startPoller(data.job_id);
      else setPulling(false);
    } catch { setPulling(false); }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Server-side sort: toggling a header re-queries page 1 so it spans all SKUs.
  const handleSort = (key: string | null) => {
    if (!key) return;
    const nextDir: 'asc' | 'desc' = sortKey === key ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc';
    setSortKey(key);
    setSortDir(nextDir);
    load(1, { sortKey: key, sortDir: nextDir });
  };

  const sortIcon = (key: string | null) => sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ⇅';

  const totalFulfillable = items.reduce((s, i) => s + i.fulfillable_quantity, 0);
  const totalInbound = items.reduce((s, i) => s + i.inbound_working_quantity + i.inbound_shipped_quantity + i.inbound_receiving_quantity, 0);
  const totalUnfulfillable = items.reduce((s, i) => s + i.unfulfillable_quantity, 0);
  const stockColor = (qty: number) => qty === 0 ? 'var(--danger)' : qty < 10 ? 'var(--warning)' : 'var(--success)';

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        {[
          { label: 'Total SKUs', value: total, accent: 'var(--accent)' },
          { label: 'Fulfillable', value: totalFulfillable, accent: 'var(--success)' },
          { label: 'Inbound', value: totalInbound, accent: 'var(--info)' },
          { label: 'Unfulfillable', value: totalUnfulfillable, accent: 'var(--danger)' },
        ].map(({ label, value, accent }) => (
          <div key={label} style={{ flex: '1 1 120px', display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderRadius: 8, background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: accent, lineHeight: 1 }}>{value}</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginBottom: 10, padding: '8px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: 13 }}>FBA Inventory</span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>via GET_FBA_MYI_UNSUPPRESSED_INVENTORY_DATA</span>
          <button className="btn btn-primary" style={{ padding: '4px 12px', fontSize: 12 }} onClick={startPull} disabled={pulling}>
            {pulling ? 'Pulling…' : '↓ Pull Now'}
          </button>
          <button className="btn btn-secondary" style={{ padding: '4px 12px', fontSize: 12, marginLeft: 'auto' }}
            onClick={() => downloadExport('/api/v1/sp-api/export/inventory.csv?type=fba', 'inventory_fba.csv')}>
            ⬇ Export All
          </button>
          <JobStatus progress={progress} noun="SKUs" />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="form-input" style={{ flex: '1 1 220px', fontSize: 12, padding: '4px 8px' }} placeholder="Search SKU, ASIN, or product name…"
          value={search} onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && load(1)} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 12 }}>
          <input type="checkbox" checked={stockFilters.includes('in_stock')} onChange={() => toggleStock('in_stock')} />
          In stock
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 12 }}>
          <input type="checkbox" checked={stockFilters.includes('out_of_stock')} onChange={() => toggleStock('out_of_stock')} />
          Out of stock
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 12 }}>
          <input type="checkbox" checked={stockFilters.includes('low_stock')} onChange={() => toggleStock('low_stock')} />
          Low stock only
        </label>
        <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => load(1)} disabled={loading}>Apply</button>
        <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => { setSearch(''); setStockFilters([]); load(1, { search: '', stockFilters: [] }); }}>Reset</button>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner" /> Loading FBA inventory…</div>
      ) : (
        <>
          <div className="card" style={{ padding: 0, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 900 }}>
              <thead>
                <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
                  {([
                    { h: 'SKU', key: 'seller_sku' },
                    { h: 'ASIN', key: 'asin' },
                    { h: 'FN SKU', key: 'fn_sku' },
                    { h: 'Product Name', key: 'product_name' },
                    { h: 'Condition', key: 'condition' },
                    { h: 'Fulfillable', key: 'fulfillable_quantity' },
                    { h: 'Inbound', key: 'inbound' },
                    { h: 'Reserved', key: 'reserved' },
                    { h: 'Unfulfillable', key: 'unfulfillable_quantity' },
                    { h: 'Country', key: 'marketplace_id' },
                    { h: 'Synced', key: 'synced_at' },
                  ] as { h: string; key: string | null }[]).map(({ h, key }) => (
                    <th key={h} onClick={() => handleSort(key)}
                      style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: sortKey === key ? 'var(--accent)' : 'var(--text-muted)', cursor: key ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}>
                      {h}{key ? <span style={{ opacity: sortKey === key ? 1 : 0.35, fontSize: 10 }}>{sortIcon(key)}</span> : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map(item => {
                  const mkCode = account.marketplaces.find(m => m.id === item.marketplace_id)?.code ?? '—';
                  return (
                  <tr key={item.id} style={{
                    borderBottom: '1px solid var(--border)',
                    background: item.fulfillable_quantity === 0 ? 'var(--danger)08' : item.fulfillable_quantity < 10 ? 'var(--warning)08' : 'transparent',
                  }}>
                    <td style={{ padding: '9px 12px', fontWeight: 600 }}><code style={{ fontSize: 12 }}>{item.seller_sku}</code></td>
                    <td style={{ padding: '9px 12px' }}><code style={{ fontSize: 12 }}>{item.asin ?? '—'}</code></td>
                    <td style={{ padding: '9px 12px', color: 'var(--text-muted)' }}><code style={{ fontSize: 11 }}>{item.fn_sku ?? '—'}</code></td>
                    <td style={{ padding: '9px 12px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.product_name ?? ''}>{item.product_name ?? '—'}</td>
                    <td style={{ padding: '9px 12px', color: 'var(--text-muted)' }}>{item.condition ?? '—'}</td>
                    <td style={{ padding: '9px 12px', fontWeight: 700, color: stockColor(item.fulfillable_quantity) }}>{item.fulfillable_quantity}</td>
                    <td style={{ padding: '9px 12px', color: 'var(--info)' }}>{item.inbound_working_quantity + item.inbound_shipped_quantity + item.inbound_receiving_quantity}</td>
                    <td style={{ padding: '9px 12px', color: 'var(--text-muted)' }}
                      title={`Customer orders: ${item.reserved_customer_orders}\nFC transfers: ${item.reserved_fc_transfers}\nFC processing: ${item.reserved_fc_processing}`}>
                      <span style={{ fontWeight: 700 }}>{item.reserved_customer_orders + item.reserved_fc_transfers + item.reserved_fc_processing}</span>
                      <div style={{ fontSize: 10, lineHeight: 1.4, color: 'var(--text-muted)', whiteSpace: 'nowrap', marginTop: 2 }}>
                        <div>Customer orders: {item.reserved_customer_orders}</div>
                        <div>FC transfers: {item.reserved_fc_transfers}</div>
                        <div>FC processing: {item.reserved_fc_processing}</div>
                      </div></td>
                    <td style={{ padding: '9px 12px', color: item.unfulfillable_quantity > 0 ? 'var(--danger)' : 'var(--text-muted)' }}>{item.unfulfillable_quantity}</td>
                    <td style={{ padding: '9px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>{mkCode}</td>
                    <td style={{ padding: '9px 12px', color: 'var(--text-muted)', fontSize: 12 }}>{new Date(item.synced_at).toLocaleDateString()}</td>
                  </tr>
                  );
                })}
                {items.length === 0 && (
                  <tr><td colSpan={11} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>No FBA inventory. Pull to get started.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
              <button className="btn btn-secondary" disabled={page <= 1} onClick={() => load(page - 1)}>← Prev</button>
              <span style={{ padding: '8px 12px', color: 'var(--text-muted)', fontSize: 13 }}>Page {page} of {totalPages}</span>
              <button className="btn btn-secondary" disabled={page >= totalPages} onClick={() => load(page + 1)}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ── FBM Panel ─────────────────────────────────────────────────────────────────

const FbmPanel: React.FC<{ account: SpAccount; marketplaceId: number | null; headers: Record<string, string> }> = ({ account, marketplaceId, headers }) => {
  const [items, setItems] = useState<FbmItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  // Independent stock-state facets; combined as OR server-side. Values:
  // 'in_stock' | 'out_of_stock' | 'low_stock'.
  const [stockFilters, setStockFilters] = useState<string[]>([]);
  const toggleStock = (v: string) => setStockFilters(s => s.includes(v) ? s.filter(x => x !== v) : [...s, v]);
  const [pulling, setPulling] = useState(false);
  const PAGE_SIZE = 50;

  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const [progress, startPoller, resetPoller] = useJobPoller(headers, () => { setPulling(false); load(1); });

  const load = useCallback(async (
    p = 1,
    overrides?: Partial<{ search: string; stockFilters: string[]; sortKey: string | null; sortDir: 'asc' | 'desc' }>,
  ) => {
    setLoading(true);
    const sk = overrides?.sortKey !== undefined ? overrides.sortKey : sortKey;
    const sd = overrides?.sortDir ?? sortDir;
    const s  = (overrides?.search ?? search).trim();
    const sf = overrides?.stockFilters ?? stockFilters;
    const params = new URLSearchParams({ page: String(p), page_size: String(PAGE_SIZE) });
    if (marketplaceId) params.set('marketplace_id', String(marketplaceId));
    if (s) params.set('search', s);
    sf.forEach(v => params.append('stock_status', v));
    if (sk) { params.set('sort_key', sk); params.set('sort_dir', sd); }
    try {
      const res = await fetch(`${API_BASE}/api/v1/sp-api/accounts/${account.seller_id}/fbm-inventory?${params}`, { headers });
      const data = await res.json();
      setItems(data.data ?? []);
      setTotal(data.total ?? 0);
      setPage(p);
    } catch { /* keep existing */ } finally { setLoading(false); }
  }, [account.seller_id, marketplaceId, search, stockFilters, sortKey, sortDir]);

  useEffect(() => { load(1); }, [account.seller_id, marketplaceId]);

  const startPull = async () => {
    setPulling(true); resetPoller();
    try {
      const mkIds = marketplaceId ? [marketplaceId] : account.marketplaces.map(m => m.id);
      const res = await fetch(`${API_BASE}/api/v1/sp-api/accounts/${account.seller_id}/pull/fbm-inventory`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ marketplace_ids: mkIds }),
      });
      const data = await res.json();
      if (data.job_id) startPoller(data.job_id);
      else setPulling(false);
    } catch { setPulling(false); }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Server-side sort: toggling a header re-queries page 1 so it spans all listings.
  const handleSort = (key: string | null) => {
    if (!key) return;
    const nextDir: 'asc' | 'desc' = sortKey === key ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc';
    setSortKey(key);
    setSortDir(nextDir);
    load(1, { sortKey: key, sortDir: nextDir });
  };

  const sortIcon = (key: string | null) => sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ⇅';

  const totalActive = items.filter(i => i.status?.toLowerCase() === 'active').length;
  const totalInactive = items.filter(i => i.status?.toLowerCase() !== 'active').length;
  const stockColor = (qty: number) => qty === 0 ? 'var(--danger)' : qty < 10 ? 'var(--warning)' : 'var(--success)';

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        {[
          { label: 'Total Listings', value: total, accent: 'var(--accent)' },
          { label: 'Active', value: totalActive, accent: 'var(--success)' },
          { label: 'Total Qty', value: items.reduce((s, i) => s + i.quantity, 0), accent: 'var(--info)' },
          { label: 'Inactive', value: totalInactive, accent: 'var(--warning)' },
        ].map(({ label, value, accent }) => (
          <div key={label} style={{ flex: '1 1 120px', display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderRadius: 8, background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: accent, lineHeight: 1 }}>{value}</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginBottom: 10, padding: '8px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: 13 }}>FBM Inventory</span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>via GET_MERCHANT_LISTINGS_ALL_DATA</span>
          <button className="btn btn-primary" style={{ padding: '4px 12px', fontSize: 12 }} onClick={startPull} disabled={pulling}>
            {pulling ? 'Pulling…' : '↓ Pull Now'}
          </button>
          <button className="btn btn-secondary" style={{ padding: '4px 12px', fontSize: 12, marginLeft: 'auto' }}
            onClick={() => downloadExport('/api/v1/sp-api/export/inventory.csv?type=fbm', 'inventory_fbm.csv')}>
            ⬇ Export All
          </button>
          <JobStatus progress={progress} noun="listings" />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="form-input" style={{ flex: '1 1 220px', fontSize: 12, padding: '4px 8px' }} placeholder="Search SKU, ASIN, or item name…"
          value={search} onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && load(1)} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 12 }}>
          <input type="checkbox" checked={stockFilters.includes('in_stock')} onChange={() => toggleStock('in_stock')} />
          In stock
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 12 }}>
          <input type="checkbox" checked={stockFilters.includes('out_of_stock')} onChange={() => toggleStock('out_of_stock')} />
          Out of stock
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 12 }}>
          <input type="checkbox" checked={stockFilters.includes('low_stock')} onChange={() => toggleStock('low_stock')} />
          Low stock only
        </label>
        <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => load(1)} disabled={loading}>Apply</button>
        <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => { setSearch(''); setStockFilters([]); load(1, { search: '', stockFilters: [] }); }}>Reset</button>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner" /> Loading FBM inventory…</div>
      ) : (
        <>
          <div className="card" style={{ padding: 0, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 900 }}>
              <thead>
                <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
                  {([
                    { h: 'SKU', key: 'seller_sku' },
                    { h: 'ASIN', key: 'asin' },
                    { h: 'Item Name', key: 'item_name' },
                    { h: 'Price', key: 'price' },
                    { h: 'Qty', key: 'quantity' },
                    { h: 'Pending', key: 'pending_quantity' },
                    { h: 'Condition', key: 'item_condition' },
                    { h: 'Status', key: 'status' },
                    { h: 'Country', key: 'marketplace_id' },
                    { h: 'Synced', key: 'synced_at' },
                  ] as { h: string; key: string | null }[]).map(({ h, key }) => (
                    <th key={h} onClick={() => handleSort(key)}
                      style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: sortKey === key ? 'var(--accent)' : 'var(--text-muted)', cursor: key ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}>
                      {h}{key ? <span style={{ opacity: sortKey === key ? 1 : 0.35, fontSize: 10 }}>{sortIcon(key)}</span> : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map(item => {
                  const mkCode = account.marketplaces.find(m => m.id === item.marketplace_id)?.code ?? '—';
                  return (
                  <tr key={item.id} style={{
                    borderBottom: '1px solid var(--border)',
                    background: item.quantity === 0 ? 'var(--danger)08' : item.quantity < 10 ? 'var(--warning)08' : 'transparent',
                  }}>
                    <td style={{ padding: '9px 12px', fontWeight: 600 }}><code style={{ fontSize: 12 }}>{item.seller_sku}</code></td>
                    <td style={{ padding: '9px 12px' }}><code style={{ fontSize: 12 }}>{item.asin ?? '—'}</code></td>
                    <td style={{ padding: '9px 12px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.item_name ?? ''}>{item.item_name ?? '—'}</td>
                    <td style={{ padding: '9px 12px' }}>{item.price != null ? `$${item.price.toFixed(2)}` : '—'}</td>
                    <td style={{ padding: '9px 12px', fontWeight: 700, color: stockColor(item.quantity) }}>{item.quantity}</td>
                    <td style={{ padding: '9px 12px', color: 'var(--text-muted)' }}>{item.pending_quantity}</td>
                    <td style={{ padding: '9px 12px', color: 'var(--text-muted)' }}>{item.item_condition ?? '—'}</td>
                    <td style={{ padding: '9px 12px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                        background: item.status?.toLowerCase() === 'active' ? 'var(--success)20' : 'var(--danger)20',
                        color: item.status?.toLowerCase() === 'active' ? 'var(--success)' : 'var(--danger)',
                      }}>{item.status ?? '—'}</span>
                    </td>
                    <td style={{ padding: '9px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>{mkCode}</td>
                    <td style={{ padding: '9px 12px', color: 'var(--text-muted)', fontSize: 12 }}>{new Date(item.synced_at).toLocaleDateString()}</td>
                  </tr>
                  );
                })}
                {items.length === 0 && (
                  <tr><td colSpan={10} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>No FBM listings. Pull to get started.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
              <button className="btn btn-secondary" disabled={page <= 1} onClick={() => load(page - 1)}>← Prev</button>
              <span style={{ padding: '8px 12px', color: 'var(--text-muted)', fontSize: 13 }}>Page {page} of {totalPages}</span>
              <button className="btn btn-secondary" disabled={page >= totalPages} onClick={() => load(page + 1)}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const SpInventory: React.FC<Props> = ({ account, marketplaceId }) => {
  const token = localStorage.getItem('access_token') ?? '';
  const headers = { Authorization: `Bearer ${token}` };
  const [activeTab, setActiveTab] = useState<InventoryTab>('fba');

  const tabStyle = (t: InventoryTab) => ({
    padding: '8px 18px', cursor: 'pointer',
    color: activeTab === t ? 'var(--primary)' : 'var(--text-muted)',
    fontWeight: activeTab === t ? 700 : 400,
    fontSize: 14, background: 'none', border: 'none',
    borderBottom: `2px solid ${activeTab === t ? 'var(--primary)' : 'transparent'}`,
  } as React.CSSProperties);

  return (
    <div>
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 10 }}>
        <button style={tabStyle('fba')} onClick={() => setActiveTab('fba')}>FBA Inventory</button>
        <button style={tabStyle('fbm')} onClick={() => setActiveTab('fbm')}>FBM Inventory</button>
      </div>
      {activeTab === 'fba'
        ? <FbaPanel account={account} marketplaceId={marketplaceId} headers={headers} />
        : <FbmPanel account={account} marketplaceId={marketplaceId} headers={headers} />
      }
    </div>
  );
};

export default SpInventory;
